import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ElementDisplayContextType } from 'components/metric/chart/MetricChartWrapper';
import {
    AnyElementsViewerDataType,
    buildElementURLOnFilters,
    ElementViewerDataResponse,
    ElementViewerDataType,
    prepareSegments,
} from 'components/element-viewer';
import { useEffect, useState } from 'react';
import { DashboardElementType } from 'tools/types';
import { MetricViewerDataType } from 'components/metric/MetricViewer';
import { ExternalReportViewerDataType } from 'app/extreport/ExternalReportViewer';
import { ReportViewerDataType } from 'app/report/ReportViewer';
import { ExternalContentViewerDataType } from 'app/ext-content/ExternalContentViewer';
import { useApplyFilterValueMap } from 'components/external-reference/Filter';

export function usePrepareViewerData(
    userChartOverlay: number,
    setUserChartOverlay: any,
    setFilters: any,
    elementDisplayType: ElementDisplayContextType,
    viewerRequestData?: ElementViewerDataResponse,
) {
    const location = useLocation();
    const isApp = useSelector((state: any) => state.elementViewer?.isApp);

    const [elementType, setElementType] = useState<DashboardElementType>();

    const segmentMatch = location.pathname.match(/\/segment\/([0-9]+)/);
    const sv = segmentMatch && !isNaN(Number(segmentMatch[1])) ? Number(segmentMatch[1]) : 0;

    const [segmentValueId, setSegmentValueId] = useState<number>(sv);

    const [viewerData, setViewerData] = useState<ElementViewerDataType>();
    const [viewerExtraData, setViewerExtraData] = useState<AnyElementsViewerDataType>();

    useEffect(() => {
        if (!viewerRequestData) {
            return;
        }

        const type = viewerRequestData.elementData.row.type;
        setElementType(type);

        setViewerData({
            elementData: viewerRequestData.elementData,
            crumbsData: viewerRequestData.crumbsData,
            segmentData: viewerRequestData.segmentData,
            segmentValueId: viewerRequestData.segmentValueId,
            targets: viewerRequestData?.targets,
            alertStatus: viewerRequestData.alertStatus,
        });

        let extraData: AnyElementsViewerDataType;
        let extra;
        switch (type) {
            case 'metric':
            case 'multi-metric chart':
                extra = viewerRequestData as ElementViewerDataType & MetricViewerDataType;
                extraData = {
                    chartData: extra.chartData,
                    manualCalendars: extra.manualCalendars,
                    metrics: extra.metrics,
                    userChartOverlay: userChartOverlay,
                    measIntervals: extra.measIntervals,
                };
                break;
            case 'external report':
                extra = viewerRequestData as ElementViewerDataType & ExternalReportViewerDataType;
                extraData = {
                    additionalData: extra.additionalData,
                    filtersData: extra.filtersData,
                    tabsData: extra.tabsData,
                    tableauCrossSite: extra.tableauCrossSite,
                };
                break;
            case 'internal report':
                extra = viewerRequestData as ElementViewerDataType & ReportViewerDataType;
                extraData = {
                    rendererFilters: extra.rendererFilters,
                    rendererBlocks: extra.rendererBlocks,
                    datasetFields: extra.datasetFields,
                    displayMasks: extra.displayMasks,
                    bookmarksList: extra.bookmarksList,
                    segmentData: extra.segmentData,
                };
                break;
            case 'other external content':
                extra = viewerRequestData as ElementViewerDataType & ExternalContentViewerDataType;
                extraData = {
                    additionalData: extra.additionalData,
                    fileCollection: extra.fileCollection,
                    fileId: extra.fileId,
                    urlsData: extra.urlsData,
                };

                break;
        }

        setViewerExtraData(extraData);
        if (elementDisplayType != 'screenshot') {
            // @ts-ignore
            const filters = viewerRequestData?.filtersData?.data?.filtersList;
            const { filtersList } = prepareSegments(filters ?? [], viewerRequestData.segmentData);
            setFilters(useApplyFilterValueMap(filtersList.slice()));
            if (elementDisplayType == 'viewer' && viewerRequestData.segmentValueId != segmentValueId && !isApp) {
                setSegmentValueId(viewerRequestData.segmentValueId);
                const newURL = buildElementURLOnFilters(
                    viewerRequestData.elementData,
                    viewerRequestData.segmentValueId,
                    userChartOverlay,
                );
                window.history.replaceState(null, document.title, newURL);
            }
        }
    }, [viewerRequestData]);

    return { elementType, viewerData, setViewerData, segmentValueId, setSegmentValueId, viewerExtraData };
}
